import React, { useEffect, useState, useRef } from "react";
import { FormControl, Select, MenuItem, CircularProgress } from "@mui/material";
import { IconButton, Typography } from "@mhome/ui";
import {
  Thermostat,
  Add,
  Remove,
  AcUnit,
  LocalFireDepartment,
} from "@mui/icons-material";
import { SyncBadge } from "../../common/badges";
import { useRecoilValue } from "recoil";
import { scopePreferenceState, activeScopeState } from "../../global-state";
import { alpha } from "@mui/material/styles";
import { hasValidWidgetItem } from "../common/util";

const Thermostat1Widget = ({ widget, onValueUpdate, pattern }) => {
  const [mode, setMode] = useState("off");
  const [coolingSetpoint, setCoolingSetpoint] = useState(75);
  const [heatingSetpoint, setHeatingSetpoint] = useState(68);
  const [currentTemp, setCurrentTemp] = useState(72);
  const [runningStatus, setRunningStatus] = useState("off");
  const [modeOptions, setModeOptions] = useState([]);
  const [widgetData, setWidgetData] = useState(null);
  const [unit, setUnit] = useState("F");
  const [activeSetpoint, setActiveSetpoint] = useState("cool"); // "heat" or "cool"
  const [isDragging, setIsDragging] = useState(null); // "heat", "cool", or null
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [containerSize, setContainerSize] = useState(200);
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const scopePreference = useRecoilValue(scopePreferenceState);
  const activeScope = useRecoilValue(activeScopeState);

  // Helper function to parse and convert temperature values
  const parseTemperature = (valueStr, targetUnit) => {
    if (!valueStr) return 0;
    const numericValue = parseFloat(
      valueStr.toString().replace(/[^0-9.-]/g, "")
    );

    // Convert to target unit if needed
    let convertedTemp = numericValue;
    if (valueStr.toString().toUpperCase().includes("F") && targetUnit === "C") {
      // Convert F to C
      convertedTemp = ((numericValue - 32) * 5) / 9;
    } else if (
      valueStr.toString().toUpperCase().includes("C") &&
      targetUnit === "F"
    ) {
      // Convert C to F
      convertedTemp = (numericValue * 9) / 5 + 32;
    }

    // Round to appropriate increment based on target unit
    const stepSize = targetUnit === "C" ? 0.5 : 1;
    convertedTemp = Math.round(convertedTemp / stepSize) * stepSize;

    return convertedTemp;
  };

  useEffect(() => {
    console.debug("widget", widget);
    if (!widget) return;
    setWidgetData(widget);

    // Determine preferred unit
    const preferredUnit =
      scopePreference?.[activeScope]?.temperatureUnit || "F";
    setUnit(preferredUnit);

    // Handle thermostat mode from $w1
    if (widget.items?.$w1) {
      const currentMode = widget.items.$w1.value || "off";
      setMode(currentMode);

      // Set mode options from enums or provide defaults
      const options = widget.items.$w1.options || [
        "off",
        "heat",
        "cool",
        "heat_cool",
      ];
      setModeOptions(options);
    }

    // Handle cooling setpoint from $w2
    if (hasValidWidgetItem(widget, "$w2")) {
      const cooling = parseTemperature(widget.items.$w2.value, preferredUnit);
      setCoolingSetpoint(cooling);
    }

    // Handle heating setpoint from $w3
    if (hasValidWidgetItem(widget, "$w3")) {
      const heating = parseTemperature(widget.items.$w3.value, preferredUnit);
      setHeatingSetpoint(heating);
    }

    // Handle current temperature from $t
    if (widget.items?.$t) {
      const temp = parseTemperature(widget.items.$t.value, preferredUnit);
      setCurrentTemp(temp);
    }

    // Handle running status from $m
    if (widget.items?.$m) {
      const status = widget.items.$m.value || "off";
      setRunningStatus(status);
    }
  }, [widget]);

  // Auto-set active setpoint based on mode
  useEffect(() => {
    if (mode === "heat") {
      setActiveSetpoint("heat");
    } else if (mode === "cool") {
      setActiveSetpoint("cool");
    }
    // For heat_cool mode, keep the current activeSetpoint
  }, [mode]);

  // Measure container size for responsive scaling
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const size = Math.min(rect.width, rect.height);
        setContainerSize(size > 0 ? size : 200); // Fallback to 200 if size is 0
      }
    };

    // Initial measurement with a small delay to ensure container is rendered
    const timeoutId = setTimeout(updateSize, 100);

    window.addEventListener("resize", updateSize);

    // Use ResizeObserver if available for better performance
    let resizeObserver;
    if (window.ResizeObserver && containerRef.current) {
      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect;
          const size = Math.min(width, height);
          // console.debug('ResizeObserver:', { width, height, size });
          setContainerSize(size > 0 ? size : 200);
        }
      });
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateSize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Calculate responsive sizes based on container
  const getResponsiveSizes = () => {
    // Balanced scaling - not too aggressive
    const baseSizes = {
      statusText: Math.max(7, containerSize * 0.045), // Reduced
      currentTemp: Math.max(10, containerSize * 0.065), // Reduced
      setpointText: Math.max(16, containerSize * 0.14), // Reduced
      unitText: Math.max(8, containerSize * 0.05), // Reduced
      icon: Math.max(10, containerSize * 0.05), // Reduced
      button: Math.max(24, containerSize * 0.14), // Keep same
      buttonIcon: Math.max(10, containerSize * 0.04), // Reduced
      dropdown: Math.max(24, containerSize * 0.09), // Keep same
      dropdownText: Math.max(9, containerSize * 0.045), // Reduced
      gap: Math.max(4, containerSize * 0.025), // Keep same
      padding: Math.max(4, containerSize * 0.035), // Keep same
    };

    return baseSizes;
  };

  const sizes = getResponsiveSizes();

  const handleModeChange = (event) => {
    const newMode = event.target.value;
    setMode(newMode);

    if (widgetData?.items?.$w1) {
      const updatedWidget = {
        ...widgetData.items.$w1,
        value: newMode,
      };
      onValueUpdate(updatedWidget);
    }
  };

  const handleSetpointChange = (direction) => {
    // Set appropriate step size based on unit
    const stepSize = unit === "C" ? 0.5 : 1;
    const delta = direction * stepSize;

    // Determine which setpoint to change based on mode
    let targetSetpoint;
    if (mode === "heat") {
      targetSetpoint = "heat";
    } else if (mode === "cool") {
      targetSetpoint = "cool";
    } else {
      // heat_cool mode - use activeSetpoint
      targetSetpoint = activeSetpoint;
    }

    const tempRange = getTempRange();

    // Clear existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (targetSetpoint === "cool") {
      let newSetpoint = coolingSetpoint + delta;

      // Apply min/max limits
      newSetpoint = Math.max(
        tempRange.coolMin,
        Math.min(tempRange.coolMax, newSetpoint)
      );

      // Apply overlap restriction only in heat_cool mode
      if (mode === "heat_cool") {
        newSetpoint = Math.max(heatingSetpoint, newSetpoint);
      }

      // Update local state immediately for responsive UI
      setCoolingSetpoint(newSetpoint);

      // Debounce the backend update
      if (widgetData?.items?.$w2) {
        debounceTimeoutRef.current = setTimeout(() => {
          const updatedWidget = {
            ...widgetData.items.$w2,
            value: newSetpoint + unit,
          };
          onValueUpdate(updatedWidget);
        }, 2000);
      }
    } else {
      let newSetpoint = heatingSetpoint + delta;

      // Apply min/max limits
      newSetpoint = Math.max(
        tempRange.heatMin,
        Math.min(tempRange.heatMax, newSetpoint)
      );

      // Apply overlap restriction only in heat_cool mode
      if (mode === "heat_cool") {
        newSetpoint = Math.min(coolingSetpoint, newSetpoint);
      }

      // Update local state immediately for responsive UI
      setHeatingSetpoint(newSetpoint);

      // Debounce the backend update
      if (widgetData?.items?.$w3) {
        debounceTimeoutRef.current = setTimeout(() => {
          const updatedWidget = {
            ...widgetData.items.$w3,
            value: newSetpoint + unit,
          };
          onValueUpdate(updatedWidget);
        }, 2000);
      }
    }
  };

  const handleSetpointClick = (setpointType) => {
    // Only allow setpoint switching in heat_cool mode
    if (mode === "heat_cool") {
      setActiveSetpoint(setpointType);
    }
  };

  // Convert mouse/touch position to temperature value
  const positionToTemp = (clientX, clientY) => {
    if (!svgRef.current) return 0;

    const rect = svgRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;

    // Calculate angle from center
    let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);

    // Normalize angle to 0-360 range
    if (angle < 0) angle += 360;

    // Convert to our ring's coordinate system (120-420 degrees)
    if (angle >= 120 && angle <= 360) {
      // Normal range 120-360
    } else if (angle >= 0 && angle <= 60) {
      // Wrap around: 0-60 becomes 360-420
      angle += 360;
    } else {
      // Outside valid range, clamp to nearest edge
      if (angle < 120) angle = 120;
      else angle = 60; // This becomes 420
    }

    // Clamp to ring range (120-420 degrees)
    angle = Math.max(120, Math.min(420, angle));

    // Convert angle to temperature
    const { min, max } = getTempRange();
    const normalizedAngle = (angle - 120) / 300; // 0-1 range
    const temp = min + normalizedAngle * (max - min);

    return temp; // Return precise value for smooth dragging
  };

  const handleDragStart = (setpointType, event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(setpointType);
    setActiveSetpoint(setpointType); // Always activate the setpoint being dragged

    const clientX = event.clientX || event.touches?.[0]?.clientX;
    const clientY = event.clientY || event.touches?.[0]?.clientY;
    setDragStartPos({ x: clientX, y: clientY });
  };

  const handleDragMove = (event) => {
    if (!isDragging) return;

    event.preventDefault();
    const clientX = event.clientX || event.touches?.[0]?.clientX;
    const clientY = event.clientY || event.touches?.[0]?.clientY;

    const newTemp = positionToTemp(clientX, clientY);

    // Apply setpoint restrictions without rounding for smooth movement
    const tempRange = getTempRange();
    let clampedTemp = newTemp;

    if (isDragging === "heat") {
      // Apply min/max limits
      clampedTemp = Math.max(
        tempRange.heatMin,
        Math.min(tempRange.heatMax, clampedTemp)
      );

      // Apply overlap restriction only in heat_cool mode
      if (mode === "heat_cool") {
        clampedTemp = Math.min(clampedTemp, coolingSetpoint);
      }

      setHeatingSetpoint(clampedTemp);
    } else if (isDragging === "cool") {
      // Apply min/max limits
      clampedTemp = Math.max(
        tempRange.coolMin,
        Math.min(tempRange.coolMax, clampedTemp)
      );

      // Apply overlap restriction only in heat_cool mode
      if (mode === "heat_cool") {
        clampedTemp = Math.max(clampedTemp, heatingSetpoint);
      }

      setCoolingSetpoint(clampedTemp);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    // Round final values for backend update (step-based)
    const stepSize = unit === "C" ? 0.5 : 1;
    const finalHeatingSetpoint =
      Math.round(heatingSetpoint / stepSize) * stepSize;
    const finalCoolingSetpoint =
      Math.round(coolingSetpoint / stepSize) * stepSize;

    // Update local state with rounded values
    setHeatingSetpoint(finalHeatingSetpoint);
    setCoolingSetpoint(finalCoolingSetpoint);

    // Update the backend with final rounded value
    if (isDragging === "heat" && widgetData?.items?.$w3) {
      const updatedWidget = {
        ...widgetData.items.$w3,
        value: finalHeatingSetpoint + unit,
      };
      onValueUpdate(updatedWidget);
    } else if (isDragging === "cool" && widgetData?.items?.$w2) {
      const updatedWidget = {
        ...widgetData.items.$w2,
        value: finalCoolingSetpoint + unit,
      };
      onValueUpdate(updatedWidget);
    }

    setIsDragging(null);
  };

  // Add global event listeners for drag operations
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();
      const handleTouchMove = (e) => handleDragMove(e);
      const handleTouchEnd = () => handleDragEnd();

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, heatingSetpoint, coolingSetpoint, widgetData, unit]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Get temperature ranges for widget - provides overall range and specific heat/cool limits
  const getTempRange = () => {
    // Get cooling limits from widget item $w2 and convert to current unit
    const coolingMinRaw =
      widgetData?.items?.$w2?.min ?? (unit === "C" ? 7 : 45);
    const coolingMaxRaw =
      widgetData?.items?.$w2?.max ?? (unit === "C" ? 32 : 90);
    const coolingUnit = widgetData?.items?.$w2?.unit || unit;
    const coolingMin = parseTemperature(coolingMinRaw + coolingUnit, unit);
    const coolingMax = parseTemperature(coolingMaxRaw + coolingUnit, unit);

    // Get heating limits from widget item $w3 and convert to current unit
    const heatingMinRaw =
      widgetData?.items?.$w3?.min ?? (unit === "C" ? 7 : 45);
    const heatingMaxRaw =
      widgetData?.items?.$w3?.max ?? (unit === "C" ? 32 : 90);
    const heatingUnit = widgetData?.items?.$w3?.unit || unit;
    const heatingMin = parseTemperature(heatingMinRaw + heatingUnit, unit);
    const heatingMax = parseTemperature(heatingMaxRaw + heatingUnit, unit);

    // Calculate overall range that encompasses both heating and cooling for ring display
    const overallMin = Math.min(coolingMin, heatingMin);
    const overallMax = Math.max(coolingMax, heatingMax);

    return {
      // Overall range for ring display
      min: overallMin,
      max: overallMax,
      // Specific limits for each setpoint
      heatMin: heatingMin,
      heatMax: heatingMax,
      coolMin: coolingMin,
      coolMax: coolingMax,
    };
  };

  // Convert temperature to angle on the ring (300 degrees total, starting from 120 degrees)
  const tempToAngle = (temp) => {
    const { min, max } = getTempRange();
    const ratio = (temp - min) / (max - min);
    // Ring starts at 120 degrees (left bottom) and goes 300 degrees clockwise
    return 120 + ratio * 300;
  };

  // Get ring position for setpoint indicators (on the ring itself)
  const getSetpointPosition = (temp, radius = 85) => {
    const angle = tempToAngle(temp);
    const radian = (angle * Math.PI) / 180;
    const x = radius * Math.cos(radian);
    const y = radius * Math.sin(radian);
    return { x, y, angle };
  };

  const getStatusIcon = () => {
    switch (runningStatus) {
      case "cool":
        return (
          <AcUnit style={{ fontSize: `${sizes.icon}px`, color: "#2196F3" }} />
        );
      case "heat":
        return (
          <LocalFireDepartment
            style={{ fontSize: `${sizes.icon}px`, color: "#FF6B35" }}
          />
        );
      default:
        return (
          <Thermostat
            style={{
              fontSize: `${sizes.icon}px`,
              color: "hsl(var(--muted-foreground))",
            }}
          />
        );
    }
  };

  const getStatusText = () => {
    if (mode === "off") {
      return "Off";
    }
    switch (runningStatus) {
      case "cool":
        return "Cooling";
      case "heat":
        return "Heating";
      default:
        return "Idle";
    }
  };

  const getTempColor = (temp, target, type) => {
    if (type === "current") {
      if (runningStatus === "cool") return "#2196F3";
      if (runningStatus === "heat") return "#FF6B35";
      return "#666";
    }
    return type === "cool" ? "#2196F3" : "#FF6B35";
  };

  if (!widgetData) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-xs">Loading...</span>
      </div>
    );
  }

  const { min: minTemp, max: maxTemp } = getTempRange();
  const heatingPos = getSetpointPosition(heatingSetpoint);
  const coolingPos = getSetpointPosition(coolingSetpoint);
  const currentTempPos = getSetpointPosition(currentTemp);

  // Calculate ring segments based on setpoint positions
  const ringCircumference = 2 * Math.PI * 85; // radius = 85
  const ringArcLength = (300 / 360) * ringCircumference; // 300 degrees total

  // Convert angles to arc positions (0 to 300 degrees)
  const heatingArcPos = (tempToAngle(heatingSetpoint) - 120) / 300; // normalize to 0-1
  const coolingArcPos = (tempToAngle(coolingSetpoint) - 120) / 300; // normalize to 0-1
  const currentTempArcPos = (tempToAngle(currentTemp) - 120) / 300; // normalize to 0-1

  // Calculate base segments
  const heatingSegmentLength = heatingArcPos * ringArcLength;
  const neutralSegmentLength = (coolingArcPos - heatingArcPos) * ringArcLength;
  const coolingSegmentLength = (1 - coolingArcPos) * ringArcLength;

  // Calculate active ranges for heating/cooling
  const needsHeating = currentTemp < heatingSetpoint;
  const needsCooling = currentTemp > coolingSetpoint;

  // Active heating range (current temp to heating setpoint)
  const activeHeatingStart = needsHeating ? currentTempArcPos : heatingArcPos;
  const activeHeatingEnd = heatingArcPos;
  const activeHeatingLength = needsHeating
    ? (activeHeatingEnd - activeHeatingStart) * ringArcLength
    : 0;

  // Active cooling range (cooling setpoint to current temp)
  const activeCoolingStart = coolingArcPos;
  const activeCoolingEnd = needsCooling ? currentTempArcPos : coolingArcPos;
  const activeCoolingLength = needsCooling
    ? (activeCoolingEnd - activeCoolingStart) * ringArcLength
    : 0;

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col items-center justify-between overflow-hidden rounded-lg"
      style={{
        height: "100%",
        width: "100%",
        gap: `${sizes.gap}px`,
        padding: `${sizes.padding}px`,
        userSelect: isDragging ? "none" : "auto",
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {pattern && pattern.endsWith("_W") && <SyncBadge />}

      {/* Responsive Circular Thermostat Display */}
      <div className="relative w-full aspect-square max-w-full max-h-full flex items-center justify-center flex-1 min-h-0 min-w-0 overflow-hidden">
        {/* SVG Ring with Temperature Scale */}
        <svg
          ref={svgRef}
          viewBox="0 0 200 200"
          className="w-full h-full absolute select-none"
        >
          {mode === "off" ? (
            /* Single neutral ring when off */
            <circle
              cx="100"
              cy="100"
              r="85"
              fill="none"
              stroke="#E0E0E0"
              strokeWidth="12"
              strokeDasharray={`${ringArcLength} ${ringCircumference}`}
              strokeDashoffset="0"
              transform="rotate(120 100 100)"
            />
          ) : mode === "heat" ? (
            /* Heat mode - show only heating zones (below heating setpoint) */
            <>
              {/* Base ring in neutral gray */}
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="none"
                stroke="#E0E0E0"
                strokeWidth="12"
                strokeDasharray={`${ringArcLength} ${ringCircumference}`}
                strokeDashoffset="0"
                transform="rotate(120 100 100)"
              />

              {/* Heating Zone (Light Orange) - From min temp to heating setpoint */}
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="none"
                stroke="#FFB380"
                strokeWidth="12"
                strokeDasharray={`${heatingSegmentLength} ${ringCircumference}`}
                strokeDashoffset="0"
                transform="rotate(120 100 100)"
              />

              {/* Active Heating Range (Bright Orange) - When current temp < heating setpoint */}
              {needsHeating && (
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#FF6B35"
                  strokeWidth="12"
                  strokeDasharray={`${activeHeatingLength} ${ringCircumference}`}
                  strokeDashoffset={`-${activeHeatingStart * ringArcLength}`}
                  transform="rotate(120 100 100)"
                />
              )}
            </>
          ) : mode === "cool" ? (
            /* Cool mode - show only cooling zones (above cooling setpoint) */
            <>
              {/* Base ring in neutral gray */}
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="none"
                stroke="#E0E0E0"
                strokeWidth="12"
                strokeDasharray={`${ringArcLength} ${ringCircumference}`}
                strokeDashoffset="0"
                transform="rotate(120 100 100)"
              />

              {/* Cooling Zone (Light Blue) - From cooling setpoint to max temp */}
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="none"
                stroke="#81C9F3"
                strokeWidth="12"
                strokeDasharray={`${coolingSegmentLength} ${ringCircumference}`}
                strokeDashoffset={`-${
                  heatingSegmentLength + neutralSegmentLength
                }`}
                transform="rotate(120 100 100)"
              />

              {/* Active Cooling Range (Bright Blue) - When current temp > cooling setpoint */}
              {needsCooling && (
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#2196F3"
                  strokeWidth="12"
                  strokeDasharray={`${activeCoolingLength} ${ringCircumference}`}
                  strokeDashoffset={`-${activeCoolingStart * ringArcLength}`}
                  transform="rotate(120 100 100)"
                />
              )}
            </>
          ) : (
            /* Heat_cool mode - show all zones */
            <>
              {/* Base Heating Zone (Light Orange) - Below heating setpoint */}
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="none"
                stroke="#FFB380"
                strokeWidth="12"
                strokeDasharray={`${heatingSegmentLength} ${ringCircumference}`}
                strokeDashoffset="0"
                transform="rotate(120 100 100)"
              />

              {/* Neutral Zone (Gray) - Between setpoints */}
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="none"
                stroke="#E0E0E0"
                strokeWidth="12"
                strokeDasharray={`${neutralSegmentLength} ${ringCircumference}`}
                strokeDashoffset={`-${heatingSegmentLength}`}
                transform="rotate(120 100 100)"
              />

              {/* Base Cooling Zone (Light Blue) - Above cooling setpoint */}
              <circle
                cx="100"
                cy="100"
                r="85"
                fill="none"
                stroke="#81C9F3"
                strokeWidth="12"
                strokeDasharray={`${coolingSegmentLength} ${ringCircumference}`}
                strokeDashoffset={`-${
                  heatingSegmentLength + neutralSegmentLength
                }`}
                transform="rotate(120 100 100)"
              />

              {/* Active Heating Range (Bright Orange) - When current temp < heating setpoint */}
              {needsHeating && (
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#FF6B35"
                  strokeWidth="12"
                  strokeDasharray={`${activeHeatingLength} ${ringCircumference}`}
                  strokeDashoffset={`-${activeHeatingStart * ringArcLength}`}
                  transform="rotate(120 100 100)"
                />
              )}

              {/* Active Cooling Range (Bright Blue) - When current temp > cooling setpoint */}
              {needsCooling && (
                <circle
                  cx="100"
                  cy="100"
                  r="85"
                  fill="none"
                  stroke="#2196F3"
                  strokeWidth="12"
                  strokeDasharray={`${activeCoolingLength} ${ringCircumference}`}
                  strokeDashoffset={`-${activeCoolingStart * ringArcLength}`}
                  transform="rotate(120 100 100)"
                />
              )}
            </>
          )}

          {/* Invisible Hit Targets - Larger interaction areas (rendered first, behind visual indicators) */}
          {(mode === "cool" || mode === "heat_cool") && (
            <circle
              cx={100 + coolingPos.x}
              cy={100 + coolingPos.y}
              r="20"
              fill="transparent"
              stroke="none"
              onMouseDown={(e) => handleDragStart("cool", e)}
              onTouchStart={(e) => handleDragStart("cool", e)}
              style={{
                cursor: "pointer",
                pointerEvents: "all",
              }}
            />
          )}

          {(mode === "heat" || mode === "heat_cool") && (
            <circle
              cx={100 + heatingPos.x}
              cy={100 + heatingPos.y}
              r="20"
              fill="transparent"
              stroke="none"
              onMouseDown={(e) => handleDragStart("heat", e)}
              onTouchStart={(e) => handleDragStart("heat", e)}
              style={{
                cursor: "pointer",
                pointerEvents: "all",
              }}
            />
          )}

          {/* Visual Setpoint Indicators - Smaller, visual-only elements */}
          {(mode === "cool" || mode === "heat_cool") && (
            <circle
              cx={100 + coolingPos.x}
              cy={100 + coolingPos.y}
              r={isDragging === "cool" ? "7" : "5"}
              fill="white"
              stroke="#2196F3"
              strokeWidth={isDragging === "cool" ? "3" : "2"}
              style={{
                pointerEvents: "none", // Visual only, no interaction
                filter:
                  isDragging === "cool"
                    ? "drop-shadow(0 0 4px rgba(33,150,243,0.6))"
                    : "none",
              }}
            />
          )}

          {(mode === "heat" || mode === "heat_cool") && (
            <circle
              cx={100 + heatingPos.x}
              cy={100 + heatingPos.y}
              r={isDragging === "heat" ? "7" : "5"}
              fill="white"
              stroke="#FF6B35"
              strokeWidth={isDragging === "heat" ? "3" : "2"}
              style={{
                pointerEvents: "none", // Visual only, no interaction
                filter:
                  isDragging === "heat"
                    ? "drop-shadow(0 0 4px rgba(255,107,53,0.6))"
                    : "none",
              }}
            />
          )}

          {/* Current Temperature Indicator */}
          <circle
            cx={100 + currentTempPos.x}
            cy={100 + currentTempPos.y}
            r="2"
            fill="#666"
            stroke="white"
            strokeWidth="1.5"
            style={{
              pointerEvents: "none", // Visual only
            }}
          />
        </svg>

        {/* Center Content */}
        <div
          className="flex flex-col items-center justify-center z-10"
          style={{ gap: `${sizes.gap}px` }}
        >
          {/* Spacer to maintain layout */}
          <div style={{ height: `${sizes.statusText}px` }} />

          {/* Current Temperature */}
          <div className="flex items-center" style={{ gap: `${sizes.gap}px` }}>
            {getStatusIcon()}
            <Typography
              className="font-semibold"
              style={{
                fontSize: `${sizes.currentTemp}px`,
                color: getTempColor(currentTemp, 0, "current"),
              }}
            >
              {currentTemp.toFixed(1)}°{unit}
            </Typography>
          </div>

          {/* Large Setpoint Display - Show based on mode */}
          {mode !== "off" && (
            <div
              className="flex items-baseline"
              style={{ gap: `${sizes.gap * 2}px` }}
            >
              {/* Heating Setpoint - Show in heat mode or heat_cool mode */}
              {(mode === "heat" || mode === "heat_cool") && (
                <div
                  onClick={() => handleSetpointClick("heat")}
                  className="inline-flex items-baseline leading-none"
                  style={{
                    fontSize: `${sizes.setpointText}px`,
                    fontWeight: "bold",
                    color:
                      mode === "heat"
                        ? "#FF6B35"
                        : activeSetpoint === "heat"
                        ? "#FF6B35"
                        : "#999",
                    cursor: mode === "heat_cool" ? "pointer" : "default",
                  }}
                >
                  {Math.floor(heatingSetpoint)}
                  <div
                    className="relative inline-flex items-baseline justify-center"
                    style={{
                      width: `${sizes.unitText * 1.5}px`,
                      height: `${sizes.setpointText * 0.8}px`,
                    }}
                  >
                    <Typography
                      component="span"
                      className="absolute top-0 left-1/2 -translate-x-1/2 leading-none font-normal"
                      style={{
                        fontSize: `${sizes.unitText}px`,
                        color: "hsl(var(--muted-foreground))",
                      }}
                    >
                      °{unit}
                    </Typography>
                    <Typography
                      component="span"
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 leading-none font-bold"
                      style={{
                        fontSize: `${sizes.setpointText * 0.4}px`,
                        color: "inherit",
                        visibility:
                          Math.abs((heatingSetpoint % 1) - 0.5) < 0.25
                            ? "visible"
                            : "hidden",
                      }}
                    >
                      .5
                    </Typography>
                  </div>
                </div>
              )}

              {/* Cooling Setpoint - Show in cool mode or heat_cool mode */}
              {(mode === "cool" || mode === "heat_cool") && (
                <div
                  onClick={() => handleSetpointClick("cool")}
                  className="inline-flex items-baseline leading-none"
                  style={{
                    fontSize: `${sizes.setpointText}px`,
                    fontWeight: "bold",
                    color:
                      mode === "cool"
                        ? "#2196F3"
                        : activeSetpoint === "cool"
                        ? "#2196F3"
                        : "#999",
                    cursor: mode === "heat_cool" ? "pointer" : "default",
                  }}
                >
                  {Math.floor(coolingSetpoint)}
                  <div
                    className="relative inline-flex items-baseline justify-center"
                    style={{
                      width: `${sizes.unitText * 1.5}px`,
                      height: `${sizes.setpointText * 0.8}px`,
                    }}
                  >
                    <Typography
                      component="span"
                      className="absolute top-0 left-1/2 -translate-x-1/2 leading-none font-normal"
                      style={{
                        fontSize: `${sizes.unitText}px`,
                        color: "hsl(var(--muted-foreground))",
                      }}
                    >
                      °{unit}
                    </Typography>
                    <Typography
                      component="span"
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 leading-none font-bold"
                      style={{
                        fontSize: `${sizes.setpointText * 0.4}px`,
                        color: "inherit",
                        visibility:
                          Math.abs((coolingSetpoint % 1) - 0.5) < 0.25
                            ? "visible"
                            : "hidden",
                      }}
                    >
                      .5
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Control Buttons - Below current temperature - Only show when not off */}
          {mode !== "off" && (
            <div
              className="flex items-center"
              style={{
                gap: `${sizes.gap * 2}px`,
                marginTop: `${sizes.gap}px`,
              }}
            >
              <IconButton
                onClick={() => handleSetpointChange(-1)}
                className="bg-transparent transition-colors duration-200"
                style={{
                  width: `${sizes.button}px`,
                  height: `${sizes.button}px`,
                  border: `2px solid ${
                    mode === "heat"
                      ? "#FF6B35"
                      : mode === "cool"
                      ? "#2196F3"
                      : activeSetpoint === "heat"
                      ? "#FF6B35"
                      : "#2196F3"
                  }`,
                  color:
                    mode === "heat"
                      ? "#FF6B35"
                      : mode === "cool"
                      ? "#2196F3"
                      : activeSetpoint === "heat"
                      ? "#FF6B35"
                      : "#2196F3",
                }}
                onMouseEnter={(e) => {
                  const hoverColor =
                    mode === "heat"
                      ? "#FF6B35"
                      : mode === "cool"
                      ? "#2196F3"
                      : activeSetpoint === "heat"
                      ? "#FF6B35"
                      : "#2196F3";
                  e.currentTarget.style.backgroundColor = hoverColor;
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  const originalColor =
                    mode === "heat"
                      ? "#FF6B35"
                      : mode === "cool"
                      ? "#2196F3"
                      : activeSetpoint === "heat"
                      ? "#FF6B35"
                      : "#2196F3";
                  e.currentTarget.style.color = originalColor;
                }}
              >
                <Remove style={{ fontSize: `${sizes.buttonIcon}px` }} />
              </IconButton>

              <IconButton
                onClick={() => handleSetpointChange(1)}
                className="bg-transparent transition-colors duration-200"
                style={{
                  width: `${sizes.button}px`,
                  height: `${sizes.button}px`,
                  border: `2px solid ${
                    mode === "heat"
                      ? "#FF6B35"
                      : mode === "cool"
                      ? "#2196F3"
                      : activeSetpoint === "heat"
                      ? "#FF6B35"
                      : "#2196F3"
                  }`,
                  color:
                    mode === "heat"
                      ? "#FF6B35"
                      : mode === "cool"
                      ? "#2196F3"
                      : activeSetpoint === "heat"
                      ? "#FF6B35"
                      : "#2196F3",
                }}
                onMouseEnter={(e) => {
                  const hoverColor =
                    mode === "heat"
                      ? "#FF6B35"
                      : mode === "cool"
                      ? "#2196F3"
                      : activeSetpoint === "heat"
                      ? "#FF6B35"
                      : "#2196F3";
                  e.currentTarget.style.backgroundColor = hoverColor;
                  e.currentTarget.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  const originalColor =
                    mode === "heat"
                      ? "#FF6B35"
                      : mode === "cool"
                      ? "#2196F3"
                      : activeSetpoint === "heat"
                      ? "#FF6B35"
                      : "#2196F3";
                  e.currentTarget.style.color = originalColor;
                }}
              >
                <Add style={{ fontSize: `${sizes.buttonIcon}px` }} />
              </IconButton>
            </div>
          )}

          {/* Mode Selection - Below control buttons */}
          <FormControl
            size="small"
            style={{
              minWidth: `${Math.max(80, sizes.dropdown * 2.5)}px`,
              marginTop: `${sizes.gap}px`,
            }}
          >
            <Select
              value={mode}
              onChange={handleModeChange}
              displayEmpty
              style={{
                height: `${sizes.dropdown}px`,
                fontSize: `${sizes.dropdownText}px`,
                borderRadius: "1rem",
              }}
              className="thermostat-select"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                  className: "thermostat-select-menu",
                },
              }}
            >
              {modeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <Typography
                    className="capitalize font-medium"
                    style={{
                      fontSize: `${sizes.dropdownText}px`,
                    }}
                  >
                    {option === "heat_cool" ? "Auto" : option}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
    </div>
  );
};

export default Thermostat1Widget;
